import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Filter, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ brand: '', fuel: '', transmission: '', priceMax: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/vehicles?status=disponible&include_recent_sold=true`);
        setVehicles(response.data);
      } catch (error) {
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => { document.title = 'Véhicules d\'Occasion | MCA CAR Quarouble'; }, []);

  const filteredVehicles = vehicles.filter(vehicle => {
    if (filters.brand && vehicle.brand.toLowerCase() !== filters.brand.toLowerCase()) return false;
    if (filters.fuel && vehicle.fuel.toLowerCase() !== filters.fuel.toLowerCase()) return false;
    if (filters.transmission && vehicle.transmission.toLowerCase() !== filters.transmission.toLowerCase()) return false;
    if (filters.priceMax && vehicle.price > parseInt(filters.priceMax)) return false;
    return true;
  });

  const uniqueBrands = [...new Set(vehicles.map(v => v.brand))];
  const uniqueFuels = [...new Set(vehicles.map(v => v.fuel))];
  const uniqueTransmissions = [...new Set(vehicles.map(v => v.transmission))];
  const clearFilters = () => setFilters({ brand: '', fuel: '', transmission: '', priceMax: '' });
  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const selectClasses = "px-4 py-2.5 text-sm focus:outline-none transition-all border border-white/10 text-zinc-300";

  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)', minHeight: '100vh' }} data-testid="vehicles-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-24 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1705747401901-28363172fe7e?auto=format&fit=crop&w=1920&q=60')` }} data-testid="vehicles-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#0A0A0A]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="section-label">Notre Parc Automobile</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-3 mb-4 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Véhicules <span className="text-[#DC2626]">d'Occasion</span>
            </h1>
            <p className="text-zinc-400 max-w-xl text-lg">
              Découvrez notre sélection de véhicules révisés, garantis et prêts à partir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters & Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Filter Bar */}
          <div className="border border-white/5 p-4 mb-8" style={{ background: 'var(--bg-surface)' }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Filter size={18} className="text-zinc-500" />
                <span className="font-semibold text-white text-sm">Filtres</span>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-[#DC2626] text-xs flex items-center gap-1 hover:underline" data-testid="clear-filters">
                    <X size={14} /> Effacer
                  </button>
                )}
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden text-[#DC2626] text-sm font-medium">
                {showFilters ? 'Masquer' : 'Afficher'} les filtres
              </button>
              <div className={`${showFilters ? 'flex' : 'hidden'} lg:flex flex-wrap gap-3 w-full lg:w-auto mt-3 lg:mt-0`}>
                <select value={filters.brand} onChange={e => setFilters({...filters, brand: e.target.value})} className={selectClasses} style={{ background: 'var(--bg-elevated)' }} data-testid="filter-brand">
                  <option value="">Toutes marques</option>
                  {uniqueBrands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={filters.fuel} onChange={e => setFilters({...filters, fuel: e.target.value})} className={selectClasses} style={{ background: 'var(--bg-elevated)' }} data-testid="filter-fuel">
                  <option value="">Tous carburants</option>
                  {uniqueFuels.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={filters.transmission} onChange={e => setFilters({...filters, transmission: e.target.value})} className={selectClasses} style={{ background: 'var(--bg-elevated)' }} data-testid="filter-transmission">
                  <option value="">Toutes boîtes</option>
                  {uniqueTransmissions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filters.priceMax} onChange={e => setFilters({...filters, priceMax: e.target.value})} className={selectClasses} style={{ background: 'var(--bg-elevated)' }} data-testid="filter-price">
                  <option value="">Budget max</option>
                  <option value="5000">5 000 €</option>
                  <option value="10000">10 000 €</option>
                  <option value="15000">15 000 €</option>
                  <option value="20000">20 000 €</option>
                  <option value="30000">30 000 €</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          {(() => {
            const count = filteredVehicles.filter(v => v.status === 'disponible').length;
            return <p className="text-zinc-500 text-sm mb-6" data-testid="results-count">{count} véhicule{count > 1 ? 's' : ''} disponible{count > 1 ? 's' : ''}</p>;
          })()}

          {/* Grid */}
          {loading && (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          )}
          {!loading && filteredVehicles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vehicles-grid">
              {filteredVehicles.map((vehicle, index) => (
                <motion.div
                  key={vehicle.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`vehicle-card group ${vehicle.status === 'vendu' ? 'opacity-60' : ''}`}
                  data-testid={`vehicle-${vehicle.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400'} alt={vehicle.title} className="vehicle-image" />
                    {vehicle.status === 'vendu' ? (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-lg font-black px-6 py-2 uppercase tracking-widest transform -rotate-6" data-testid={`sold-badge-${vehicle.id}`}>VENDU</span>
                      </div>
                    ) : (
                      <span className="absolute top-3 right-3 price-badge text-sm">{vehicle.price.toLocaleString('fr-FR')} €</span>
                    )}
                    {vehicle.featured && vehicle.status !== 'vendu' && (
                      <span className="absolute top-3 left-3 bg-yellow-500 text-white text-xs font-bold px-3 py-1">En vedette</span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-1">{vehicle.title}</h3>
                    <p className="text-zinc-500 text-xs mb-3" style={{ fontFamily: 'var(--font-mono)' }}>{vehicle.brand} {vehicle.model}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="badge">{vehicle.year}</span>
                      <span className="badge">{vehicle.mileage.toLocaleString('fr-FR')} km</span>
                      <span className="badge">{vehicle.fuel}</span>
                      <span className="badge">{vehicle.transmission}</span>
                    </div>
                    {vehicle.status === 'vendu' ? (
                      <span className="block w-full text-center py-2.5 text-xs text-zinc-600 border border-white/5">Ce véhicule a été vendu</span>
                    ) : (
                      <Link to={`/vehicules/${vehicle.id}`} className="block w-full text-center py-2.5 bg-[#DC2626] text-white text-sm font-semibold transition-all duration-200 hover:bg-[#B91C1C]" data-testid={`view-vehicle-${vehicle.id}`}>
                        Voir le véhicule
                      </Link>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {!loading && filteredVehicles.length === 0 && (
            <div className="text-center py-20 border border-white/5" style={{ background: 'var(--bg-surface)' }}>
              <Package className="mx-auto text-zinc-600 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-white mb-2">Aucun véhicule trouvé</h3>
              <p className="text-zinc-500 mb-6">{hasActiveFilters ? "Essayez de modifier vos filtres" : "Aucun véhicule disponible pour le moment"}</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-[#DC2626] font-semibold hover:underline">Effacer les filtres</button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Vehicles;
